
import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import { RootState } from "../../store";
import { debugData } from "../../utils/debugData";
import { isEnvBrowser } from "../../utils/misc";
import { useSocketEventOutsideRequest } from "../../hooks/useSocketEventOutsideRequest";
import { startHideEvent } from "../../utils/startHideEvent";
import { socketRequest } from "../../utils/socketRequest";

import { UserState, SET_PLAYERDATA } from "../../reducers/userReducer";
import { PermissionType, PlayerType, HardMoneyState, SET_HOUSEDATA, DEV_SET_isAlarmActive } from "../../reducers/hardmoneyReducer";
import { UIState, SET_activeTab, SET_hidden, SET_isLoading } from "../../reducers/uiReducer";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid, regular, light, thin, duotone, brands } from "@fortawesome/fontawesome-svg-core/import.macro";

import { throttle } from 'lodash';
import UserDropdown from './UserDropdown';
import { useAuth } from '../../context/AuthContext';

const HeaderPage: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, user: authUser } = useAuth();
    const uiData = useSelector((state: RootState) => state.ui);
    const playerData = useSelector((state: RootState) => state.user);
    const hardmoneyData = useSelector((state: RootState) => state.hardmoney);

    // Define which tabs require authentication
    const protectedTabs = ['maintenance', 'settings', 'account'];
    const socketConnected = uiData.socketConnected;

    const securityData = hardmoneyData.propertyUpgrades.find((upgrade, index) => {
        return (upgrade.id == "security")
    })

    if (!securityData) return;

    const fetchHouseData = useCallback(
        throttle(async (alarmActive: boolean) => {
            dispatch(SET_isLoading(true));
            try {
                const res: HardMoneyState = await socketRequest("SetAlarmActive", {
                    houseID: hardmoneyData.houseID,
                    houseOwner: hardmoneyData.houseOwner,
                    activateAlarm: !alarmActive,
                }, hardmoneyData);
                if (res) {
                    if (isEnvBrowser()) {
                        //console.log("SET NEW alarm active ACCOUNT",  res);
                        dispatch(DEV_SET_isAlarmActive(!alarmActive));
                    } else {
                        dispatch(SET_HOUSEDATA(res));
                    };
                } else {
                    throw new Error("Invalid alarm data received.");
                }
            } catch (err) {
                console.error("Error alarm house data:", err);
            } finally {
                dispatch(SET_isLoading(false));
            }
        }, 3000),
        [dispatch]
    );

    const fetchMoveData = useCallback(
        throttle(async (moveModeActive: boolean) => {
            dispatch(SET_isLoading(true));
            try {
                const res: UserState = await socketRequest("SetMoveMode", {
                    houseID: hardmoneyData.houseID,
                    houseOwner: hardmoneyData.houseOwner,
                    activateMoveMode: !moveModeActive,
                }, playerData);
                if (res) {
                    if (isEnvBrowser()) {
                        //console.log("SET NEW move mode ACCOUNT",  res);
                        dispatch(DEV_SET_isAlarmActive(!moveModeActive));
                    } else {
                        dispatch(SET_PLAYERDATA(res));
                    };
                } else {
                    throw new Error("Invalid move data received.");
                }
            } catch (err) {
                console.error("Error move house data:", err);
            } finally {
                dispatch(SET_isLoading(false));
            }
        }, 3000),
        [dispatch]
    );

    const handleTabClick = (e: React.MouseEvent, tabName: string) => {
        e.preventDefault();

        // Don't do anything if already on this tab
        if (uiData.activeTab === tabName) {
            return;
        }

        // Check if this tab requires authentication
        const requiresAuth = protectedTabs.includes(tabName);

        if (requiresAuth && !isAuthenticated) {
            // Tab requires auth but user is not authenticated
            // Redirect to login with the intended destination
            // DON'T use replace - we want /home to stay in history
            // so back button returns to /home, not stays at /auth/login
            navigate('/auth/login', {
                state: { from: { pathname: `/${tabName}` } }
            });
        } else if (requiresAuth && !socketConnected) {
            // Tab requires auth and user is authenticated, but websocket not connected
            // Don't navigate - just do nothing (user will see loading state when they get there)
            return;
        } else {
            // Either tab is public or (user is authenticated AND websocket connected)
            // Navigate normally
            dispatch(SET_activeTab(tabName));
            navigate(`/${tabName}`);
        }
    };

    const handleCloseClick = (e: React.MouseEvent) => {
        e.preventDefault();
        
        startHideEvent(dispatch);
    };

    const handleAlarmClick = (e: React.MouseEvent) => {
        e.preventDefault();

        fetchHouseData(hardmoneyData.isAlarmActive);
    };

    const handleToggleMoveMode = (e: React.MouseEvent) => {
        e.preventDefault();
        
        fetchMoveData(playerData.isMoveModeActive);
    };

    return (
        <div
            className="flexCenter"
            style={{
                width: "100%",
                height: "100%",
                overflow: "hidden",
                boxSizing: "border-box",
                position: "relative",
                //backgroundColor: "purple",
                //padding: "1vh",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "1.6vh",
                fontWeight: "bold",
            }}
        >
            <div
                style={{
                    backgroundColor: "rgba(60, 60, 60, 0.8)",
                    boxSizing: "border-box",
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: "5vh",
                    borderRadius: "0.5vh",
                    overflow: "hidden",
                    padding: "1.25vh 2vh",
                    border: "0.2vh solid rgba(90, 90, 90, 0.8)",
                }}
            >
                {uiData.tabs.map(tabName => (
                    <div
                        className="flexCenter"
                        onClick={(e) => handleTabClick(e, tabName)}
                        key={tabName}
                        style={{
                            height: "100%",
                            width: "fit-content",
                          //  padding: "1vh",
                            //backgroundColor: "red",
                            textAlign: "center",
                            boxSizing: "border-box",
                            borderBottom: uiData.activeTab === tabName ? `0.25vh solid rgb(${uiData.schemeColor})` : "0.25vh solid transparent",
                            color: uiData.activeTab === tabName ? `rgb(${uiData.schemeColor})` : "rgba(255, 255, 255, 0.9)",
                            transition: "border-bottom 0.3s ease, color 0.3s ease",
                            cursor: "pointer",
                        }}
                    >
                        {tabName.charAt(0).toUpperCase() + tabName.slice(1)}
                    </div>
                ))}
            </div>

            {/* toggle move mode */}
            <div
                onClick={(e) => handleToggleMoveMode(e)}
                style={{
                    border: playerData.isMoveModeActive ? `0.2vh solid rgb(227, 125, 9)` : "0.2vh solid rgba(90, 90, 90, 0.8)",
                    backgroundColor: playerData.isMoveModeActive ? `rgba(227, 125, 9, 0.2)` : "rgba(60, 60, 60, 0.8)",
                    //backgroundColor: "yellow",
                    height: "70%",
                    padding: "1vh",
                    borderRadius: "0.75vh",
                    width: "fit-content",
                    //aspectRatio: "1/1",
                    display: "flex",
                    gap: "1.5vh",
                    justifyContent: "center",
                    alignItems: "center",
                    boxSizing: "border-box",
                    verticalAlign: "middle",
                    transition: "color 0.5s ease, border 0.5s ease, background-color 0.5s ease",
                    cursor: "pointer",
                    color: "rgba(255, 255, 255, 0.9)",
                }}
            >
                {playerData.isMoveModeActive ? (
                    "Deactivate Move Mode"
                ) : (
                    "Activate Move Mode"
                )} 
                    
                {playerData.isMoveModeActive ? (
                    <FontAwesomeIcon 
                        icon={solid("turn-down-right")} 
                        style={{ width: '2vh', height: '2vh' }}
                    /> 
                ) : (
                    <FontAwesomeIcon 
                        icon={solid('up-down-left-right')} 
                        style={{ width: '2vh', height: '2vh' }}
                    /> 
                )}
            </div>

            {/* alarm button */}
            {securityData.level >= playerData.alarmLevel && (
                <div
                    onClick={(e) => handleAlarmClick(e)}
                   // className="hoverColor"
                    style={{
                        //'--scheme-color': `rgb(${uiData.schemeColor})`, 
                        border: hardmoneyData.isAlarmActive ? `0.2vh solid rgb(171, 28, 0)` : "0.2vh solid rgba(90, 90, 90, 0.8)",
                        backgroundColor: hardmoneyData.isAlarmActive ? `rgba(171, 28, 0, 0.2)` : "rgba(60, 60, 60, 0.8)",
                        //backgroundColor: "yellow",
                        height: "70%",
                        padding: "1vh",
                        borderRadius: "0.75vh",
                        width: "fit-content",
                        //aspectRatio: "1/1",
                        display: "flex",
                        gap: "1.5vh",
                        justifyContent: "center",
                        alignItems: "center",
                        boxSizing: "border-box",
                        verticalAlign: "middle",
                        transition: "color 0.5s ease, border 0.5s ease, background-color 0.5s ease",
                        cursor: "pointer",
                        color: "rgba(255, 255, 255, 0.9)",
                    } as React.CSSProperties & { '--scheme-color': string }}
                >
                    {hardmoneyData.isAlarmActive ? (
                        "Deactivate Alarm"
                    ) : (
                        "Activate Alarm"
                    )} 
                    
                    {hardmoneyData.isAlarmActive ? (
                        <FontAwesomeIcon 
                            icon={regular('light-emergency-on')} 
                            style={{ width: '2vh', height: '2vh' }}
                        /> 
                    ) : (
                        <FontAwesomeIcon 
                            icon={regular('light-emergency')} 
                            style={{ width: '2vh', height: '2vh' }}
                        /> 
                    )}
                </div>
            )}

            {/* User Dropdown */}
            {isAuthenticated && (
                <UserDropdown
                    profileImageUrl={authUser?.profileImageUrl || null}
                    firstName={authUser?.playerFirstName || ''}
                    lastName={authUser?.playerLastName || ''}
                />
            )}

            {/* close button */}
            <div
                onClick={(e) => handleCloseClick(e)}
                className="hoverColor"
                style={{
                    '--scheme-color': `rgb(${uiData.schemeColor})`,
                    //backgroundColor: "yellow",
                    height: "70%",
                    width: "fit-content",
                    //aspectRatio: "1/1",
                    display: "flex",
                    gap: "1.5vh",
                    justifyContent: "center",
                    alignItems: "center",
                    boxSizing: "border-box",
                    verticalAlign: "middle",
                    transition: "color 0.5s ease",
                    cursor: "pointer",
                } as React.CSSProperties & { '--scheme-color': string }}
            >
                Close

                <FontAwesomeIcon
                    icon={regular('power-off')}
                    style={{ width: '2vh', height: '2vh' }}
                />
            </div>
        </div>
    );
};

export default HeaderPage;
